export interface V2PlanFeatureSection {
  label?: string
  icon?: string
  iconColor?: string
  items: {
    text: string
    icon: string
    iconColor: string
  }[]
}

export interface V2PlanAddon {
  icon: string
  name: string
  badge?: string
  description: string
  price: string
}

export interface V2PlanConfig {
  name: string
  slug: string
  price?: string
  description?: string
  featuresLeft: V2PlanFeatureSection[]
  featuresRight: V2PlanFeatureSection[]
  addons: V2PlanAddon[]
}

export const OPEN_SOURCE_PLAN: V2PlanConfig = {
  name: 'Open-source plan',
  slug: 'PLAN_OPEN_SOURCE',
  description:
    'Includes 1K code review and code formatting runs across unlimited public repositories.',
  featuresLeft: [],
  featuresRight: [],
  addons: []
}

export const TEAM_PLAN: V2PlanConfig = {
  name: 'Team plan',
  slug: 'PLAN_TEAM',
  price: '$30/month/contributor',
  featuresLeft: [
    {
      items: [
        { text: 'Unlimited public repositories', icon: 'infinity', iconColor: 'juniper' },
        { text: 'Unlimited private repositories', icon: 'infinity', iconColor: 'juniper' },
        { text: 'Unlimited pull-requests', icon: 'infinity', iconColor: 'juniper' },
        { text: 'Unlimited auto code formatting', icon: 'infinity', iconColor: 'juniper' }
      ]
    },
    {
      label: 'Core Capabilities',
      icon: 'fire',
      iconColor: 'vanilla-400',
      items: [
        { text: 'Code quality analysis', icon: 'check', iconColor: 'juniper' },
        { text: 'Code security (SAST) analysis', icon: 'check', iconColor: 'juniper' },
        { text: 'Complexity analysis', icon: 'check', iconColor: 'juniper' },
        { text: 'Code coverage tracking', icon: 'check', iconColor: 'juniper' },
        { text: 'Secrets detection', icon: 'check', iconColor: 'juniper' },
        { text: 'Infrastructure-as-code analysis', icon: 'check', iconColor: 'juniper' }
      ]
    }
  ],
  featuresRight: [
    {
      label: 'Batteries Included',
      icon: 'zap',
      iconColor: 'vanilla-400',
      items: [
        { text: 'Pull request gates', icon: 'check', iconColor: 'juniper' },
        { text: 'Pull request comments and inline review', icon: 'check', iconColor: 'juniper' },
        { text: 'Org and repository reports', icon: 'check', iconColor: 'juniper' },
        { text: 'Integrations (Jira, Slack, Vanta)', icon: 'check', iconColor: 'juniper' },
        { text: 'Support for monorepos', icon: 'check', iconColor: 'juniper' },
        { text: 'API and Webhooks', icon: 'check', iconColor: 'juniper' },
        { text: 'Audit Logs', icon: 'check', iconColor: 'juniper' },
        { text: 'Priority email support', icon: 'check', iconColor: 'juniper' },
        { text: 'MCP Server', icon: 'check', iconColor: 'juniper' },
        { text: 'Single sign-on (SSO)', icon: 'check', iconColor: 'juniper' }
      ]
    }
  ],
  addons: [
    {
      icon: 'bot',
      name: 'AI Review',
      badge: '$10 credit included per contributor',
      description: "Deep code review using DeepSource's hybrid AI code review agent.",
      price: '$8/100K input lines, $4/1K fixed lines'
    },
    {
      icon: 'shield-alert',
      name: 'OSS Vulnerability Scanning',
      badge: '3 targets included',
      description: 'Find and fix CVEs in open-source dependencies.',
      price: '$9/target/month'
    }
  ]
}

export const TRIAL_PLAN: V2PlanConfig = {
  ...TEAM_PLAN,
  slug: 'PLAN_TRIAL'
}

export const ENTERPRISE_PLAN: V2PlanConfig = {
  name: 'Enterprise plan',
  slug: 'PLAN_ENTERPRISE',
  price: 'Custom pricing',
  featuresLeft: [
    {
      label: 'Includes everything in Team plan',
      icon: 'plus',
      iconColor: 'juniper',
      items: [
        { text: 'High-priority access on Enterprise Cloud', icon: 'check', iconColor: 'juniper' },
        { text: 'Self-hosted deployment', icon: 'check', iconColor: 'juniper' },
        { text: 'Live chat support with SLAs', icon: 'check', iconColor: 'juniper' },
        { text: 'Dedicated account manager', icon: 'check', iconColor: 'juniper' },
        { text: 'Migration assistance', icon: 'check', iconColor: 'juniper' },
        { text: 'Custom terms', icon: 'check', iconColor: 'juniper' },
        { text: 'Uptime SLA guarantees', icon: 'check', iconColor: 'juniper' },
        { text: 'Manual Invoicing', icon: 'check', iconColor: 'juniper' },
        { text: 'Pay via GCP Marketplace', icon: 'check', iconColor: 'juniper' }
      ]
    }
  ],
  featuresRight: [],
  addons: [
    {
      icon: 'product-icons',
      name: 'AI Review',
      description: "Deep code review using DeepSource's hybrid AI code review agent.",
      price: 'Custom pricing'
    },
    {
      icon: 'sca-vulnerabilities',
      name: 'OSS Vulnerability Scanning',
      description: 'Find and fix CVEs in open-source dependencies.',
      price: 'Custom pricing'
    }
  ]
}

export const V2_PLANS: Record<string, V2PlanConfig> = {
  PLAN_OPEN_SOURCE: OPEN_SOURCE_PLAN,
  PLAN_TEAM: TEAM_PLAN,
  PLAN_TRIAL: TRIAL_PLAN,
  PLAN_ENTERPRISE: ENTERPRISE_PLAN
}
